import { useState } from 'react';
import { X, Facebook, Twitter, Linkedin, Mail, Link as LinkIcon, MessageCircle, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const ShareModal = ({ isOpen, onClose, product, url }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const shareUrl = url || window.location.href;
  const shareTitle = `Check out ${product.name}!`;
  const shareText = `${product.name} - ${product.description.substring(0, 100)}... Only ₹${product.price.toLocaleString()}!`;

  // Copy link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  // Native share API (mobile devices)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        // User cancelled share
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      toast.error('Share not supported on this device');
    }
  };

  // Instagram sharing (mobile only - opens Instagram app)
  const handleInstagramShare = async () => {
    // Check if on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Try to open Instagram app
      const instagramUrl = `instagram://library?AssetPath=${encodeURIComponent(product.image_url)}`;
      window.location.href = instagramUrl;
      
      // Fallback: Copy link and guide user
      setTimeout(async () => {
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied! Paste it in your Instagram post or story');
        } catch (error) {
          toast.info('Copy the link and share it on Instagram!');
        }
      }, 1000);
    } else {
      // Desktop: Copy link and guide user
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied! Open Instagram on your phone to share');
      } catch (error) {
        toast.info('Instagram sharing works best on mobile. Copy the link to share!');
      }
    }
  };

  // Social media share URLs
  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareText)}&media=${encodeURIComponent(product.image_url)}`,
    email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`,
  };

  const handleSocialShare = (platform) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="share-modal"
    >
      <div 
        className="bg-gray-900 rounded-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Share Product</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition"
            data-testid="close-share-modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Product Preview */}
        <div className="flex items-center space-x-4 mb-6 p-4 glass-effect rounded-lg">
          <img 
            src={product.image_url} 
            alt={product.name}
            className="w-16 h-16 object-cover rounded-lg"
          />
          <div className="flex-1">
            <h3 className="font-semibold line-clamp-1">{product.name}</h3>
            <p className="text-red-500 font-bold">₹{product.price.toLocaleString()}</p>
          </div>
        </div>

        {/* Native Share (Mobile) */}
        {navigator.share && (
          <button
            onClick={handleNativeShare}
            className="w-full mb-4 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center space-x-2 transition"
            data-testid="native-share-btn"
          >
            <Share2 size={20} />
            <span>Share via...</span>
          </button>
        )}

        {/* Social Media Grid */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {/* Facebook */}
          <button
            onClick={() => handleSocialShare('facebook')}
            className="flex flex-col items-center space-y-2 p-4 glass-effect hover:bg-white/10 rounded-lg transition"
            data-testid="share-facebook"
          >
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <Facebook size={24} fill="white" />
            </div>
            <span className="text-xs">Facebook</span>
          </button>

          {/* Twitter */}
          <button
            onClick={() => handleSocialShare('twitter')}
            className="flex flex-col items-center space-y-2 p-4 glass-effect hover:bg-white/10 rounded-lg transition"
            data-testid="share-twitter"
          >
            <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center border border-white/20">
              <Twitter size={24} fill="white" />
            </div>
            <span className="text-xs">Twitter</span>
          </button>

          {/* WhatsApp */}
          <button
            onClick={() => handleSocialShare('whatsapp')}
            className="flex flex-col items-center space-y-2 p-4 glass-effect hover:bg-white/10 rounded-lg transition"
            data-testid="share-whatsapp"
          >
            <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
              <MessageCircle size={24} fill="white" />
            </div>
            <span className="text-xs">WhatsApp</span>
          </button>

          {/* Instagram */}
          <button
            onClick={handleInstagramShare}
            className="flex flex-col items-center space-y-2 p-4 glass-effect hover:bg-white/10 rounded-lg transition"
            data-testid="share-instagram"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-full flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </div>
            <span className="text-xs">Instagram</span>
          </button>

          {/* LinkedIn */}
          <button
            onClick={() => handleSocialShare('linkedin')}
            className="flex flex-col items-center space-y-2 p-4 glass-effect hover:bg-white/10 rounded-lg transition"
            data-testid="share-linkedin"
          >
            <div className="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center">
              <Linkedin size={24} fill="white" />
            </div>
            <span className="text-xs">LinkedIn</span>
          </button>

          {/* Pinterest */}
          <button
            onClick={() => handleSocialShare('pinterest')}
            className="flex flex-col items-center space-y-2 p-4 glass-effect hover:bg-white/10 rounded-lg transition"
            data-testid="share-pinterest"
          >
            <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
              </svg>
            </div>
            <span className="text-xs">Pinterest</span>
          </button>

          {/* Email */}
          <button
            onClick={() => handleSocialShare('email')}
            className="flex flex-col items-center space-y-2 p-4 glass-effect hover:bg-white/10 rounded-lg transition"
            data-testid="share-email"
          >
            <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center">
              <Mail size={24} />
            </div>
            <span className="text-xs">Email</span>
          </button>
        </div>

        {/* Copy Link */}
        <button
          onClick={handleCopyLink}
          className={`w-full py-3 rounded-lg flex items-center justify-center space-x-2 transition ${
            copied 
              ? 'bg-green-600 text-white' 
              : 'glass-effect hover:bg-white/10 border border-white/10'
          }`}
          data-testid="copy-link-btn"
        >
          <LinkIcon size={20} />
          <span>{copied ? 'Link Copied!' : 'Copy Link'}</span>
        </button>
      </div>
    </div>
  );
};

export default ShareModal;